import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import dropzoneImage from '../../../assets/images/dropzone.png'

import classes from './Dropzone.module.css'
import { toBytes } from '../../../util/functions'

const overrideEventDefaults = event => {
    event.preventDefault()
    event.stopPropagation()
}

// If a dropzone is present, prevent the browser from opening the file
// in case a user accidentally drops the file outside the zone
const usePreventBrowserActionWhilePresent = () => {
    useEffect(() => {
        window.addEventListener('drop', overrideEventDefaults)
        window.addEventListener('dragover', overrideEventDefaults)
        return () => {
            window.removeEventListener('drop', overrideEventDefaults)
            window.removeEventListener('dragover', overrideEventDefaults)
        }
    })
}

export const Dropzone = ({
    acceptedFileTypes = [],
    onChooseFile,
    className,
    sizeLimit = null,
    defaultSrc,
    ...props
}) => {
    const [hasDraggedItem, setHasDraggedItem] = useState(false)
    const [hasUnsupportedFileType, setHasUnsupportedFileType] = useState(false)
    const [hasUnsupportedFileSize, setHasUnsupportedFileSize] = useState(false)
    const inputRef = useRef(null)
    const getUploadedFile = () => {
        const { current } = inputRef
        if (!current) {
            return null
        }
        return current.files
            ? (current.files.length > 0 && current.files[0]) || null
            : null
    }
    const isAcceptedFileType = file => {
        if (!file.type) return false
        if (acceptedFileTypes.length === 0) {
            return true
        }
        return acceptedFileTypes.some(acceptedFileType => {
            // Special cases: audio/*, video/*, image/*
            // See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#Unique_file_type_specifiers
            if (acceptedFileType.endsWith('*')) {
                const acceptedType = acceptedFileType.split('/')[0]
                const fileType = file.type.split('/')[0]
                return acceptedType === fileType
            }
            return acceptedFileType === file.type
        })
    }
    const isAcceptedFileSize = file =>
        !sizeLimit || file.size <= toBytes(sizeLimit)
    const handleInputChange = () => {
        const file = getUploadedFile()
        if (file && !!onChooseFile) {
            if (isAcceptedFileType(file)) {
                if (!isAcceptedFileSize(file)) {
                    return setHasUnsupportedFileSize(true)
                }
                setHasUnsupportedFileType(false)
                setHasUnsupportedFileSize(false)
                onChooseFile(file)
            } else {
                setHasUnsupportedFileType(true)
            }
        }
    }
    const triggerFileSelect = () => {
        const { current } = inputRef
        if (current) {
            current.click()
        }
    }
    const handleDrop = event => {
        overrideEventDefaults(event)
        setHasDraggedItem(false)
        setHasUnsupportedFileType(false)
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            const file = event.dataTransfer.files[0]
            if (isAcceptedFileType(file)) {
                if (!isAcceptedFileSize(file)) {
                    return setHasUnsupportedFileSize(true)
                }
                setHasUnsupportedFileType(false)
                setHasUnsupportedFileSize(false)
                onChooseFile(file)
            } else {
                setHasUnsupportedFileType(true)
            }
        }
    }
    const handleDragEnter = event => {
        overrideEventDefaults(event)
        setHasDraggedItem(true)
    }
    const handleDragLeave = event => {
        overrideEventDefaults(event)
        setHasDraggedItem(false)
    }
    const accept = useMemo(() => {
        if (acceptedFileTypes.length === 0) {
            return '*'
        } else {
            return acceptedFileTypes.join(',')
        }
    }, [acceptedFileTypes])
    usePreventBrowserActionWhilePresent()
    return (
        <div
            className={classNames(classes.Dropzone, className)}
            onDrag={overrideEventDefaults}
            onDragStart={overrideEventDefaults}
            onDragEnd={overrideEventDefaults}
            onDragOver={overrideEventDefaults}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
        >
            <input
                className={classes.DropzoneFileInput}
                type="file"
                aria-hidden={true}
                ref={inputRef}
                onChange={handleInputChange}
                accept={accept}
            />
            <img
                className={classes.DropzoneImage}
                src={defaultSrc || dropzoneImage}
                alt=""
            />
            <div className={classes.DropzoneText}>
                <h5 className={classes.DropzoneTitle}>
                    {!hasDraggedItem
                        ? 'Drag and drop to upload'
                        : 'Drop here to upload'}
                </h5>
                {!hasDraggedItem && (
                    <p className={classes.DropzoneSubtitle}>
                        or{' '}
                        <button
                            type="button"
                            className={classes.DropzoneUploadLink}
                            onClick={triggerFileSelect}
                        >
                            browse
                        </button>{' '}
                        to choose a file
                    </p>
                )}
                {hasUnsupportedFileType && (
                    <p className={classes.DropzoneError}>
                        That file type is not supported.
                    </p>
                )}
                {hasUnsupportedFileSize && (
                    <p className={classes.DropzoneError}>
                        The file size is too large. The limit is {sizeLimit}mb
                    </p>
                )}
            </div>
        </div>
    )
}
